
import { useState } from "react";
import { FiDownload, FiEyeOff, FiFile, FiLock } from "react-icons/fi";
import { shortenDigits } from "utils/helpers";
import { useCurrentCompanyId, useCurrentUser } from "hooks/auth";
import { useCompanyPreviewData } from "../hooks/useCompanyPreviewData";
import type { CorporateDocument } from "newui/features/Company/types";
import type { DocumentAccessRequest } from "../hooks/useCompanyPreviewData";
import Modal from "newui/components/ui/Modal/Modal";
import { RequestAccessModal } from "components/DocumentForm/RequestAccessModal";
import { RestrictedDocumentModal } from "components/GuestModal/RestrictedDocumentModal";
import { ExtraInfo } from "newui/features/Company/types/CorporateDocument.type";

const INSURANCE_CERTIFICATES_CATEGORY_ID = 4;

interface DocumentCategoryGroupProps {
	categoryId: number;
	documents: CorporateDocument[];
	title: string;
}

type DocumentAction = "view" | "request" | "requested" | "private-no-url" | "no-file";

function getDocumentAction(
	doc: CorporateDocument,
	currentCompanyId: number | null,
	requestedDocIds: number[],
): DocumentAction {
	if (doc.display !== "private") {
		return doc.storage_path ? "view" : "no-file";
	}

	if (requestedDocIds.includes(doc.id)) {
		return "requested";
	}

	if (currentCompanyId && doc.approved_companies?.includes(currentCompanyId)) {
		return doc.storage_path ? "view" : "private-no-url";
	}

	return "request";
}

function getRequestedDocIds(
	accessRequests: DocumentAccessRequest[],
	currentCompanyId: number | null,
): number[] {
	if (!currentCompanyId) return [];
	return accessRequests
		.filter(
			(req) =>
				!req.is_viewed &&
				req.client_company?.id === currentCompanyId &&
				req.document,
		)
		.map((req) => req.document!.id);
}

function isInsuranceCategory(categoryId: number, doc: CorporateDocument): boolean {
	return (
		categoryId === INSURANCE_CERTIFICATES_CATEGORY_ID ||
		doc.category?.parent_id === INSURANCE_CERTIFICATES_CATEGORY_ID
	);
}

function getInsuranceValue(isPrivate: boolean, extraInfo?: ExtraInfo): string | null {
	if (isPrivate || !extraInfo?.value) return null;
	return `$${shortenDigits(extraInfo.value)}`;
}

export const DocumentCategoryGroup: React.FC<DocumentCategoryGroupProps> = ({
	categoryId,
	title,
	documents,
}) => {
	const currentCompanyId = useCurrentCompanyId();
	const { isGuest } = useCurrentUser() ?? { isGuest: false };
	const { documentAccessRequests } = useCompanyPreviewData();

	const requestedDocIds = getRequestedDocIds(documentAccessRequests, currentCompanyId);

	const [requestModalDocId, setRequestModalDocId] = useState<number | null>(null);
	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

	if (documents.length === 0) { return null; }

	const isInsurance = isInsuranceCategory(categoryId, documents[0]);
	const colCount = isInsurance ? 5 : 3;
	const gridCols = isInsurance
		? "grid-cols-[auto_auto_auto_auto_1fr]"
		: "grid-cols-[auto_1fr_auto]";

	const renderActionBadge = (action: DocumentAction) => {
		if (action === "view") {
			return (
				<span className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-blue-600 ring-1 ring-inset ring-blue-200">
					<FiDownload size={14} />
					View Document
				</span>
			);
		}
		if (action === "requested") {
			return (
				<span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 ring-1 ring-inset ring-red-200">
					<FiLock size={14} />
					Requested
				</span>
			);
		}
		if (action === "request") {
			return (
				<span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 ring-1 ring-inset ring-red-200">
					<FiEyeOff size={14} />
					Request Access
				</span>
			);
		}
		if (action === "private-no-url") {
			return (
				<span className="inline-flex items-center gap-1.5 text-gray-400">
					<FiEyeOff size={16} title="Private document" />
				</span>
			);
		}
		return null;
	};

	const renderRow = (doc: CorporateDocument) => {
		const action = getDocumentAction(doc, currentCompanyId, requestedDocIds);
		const isClickable = action === "view" || action === "request";
		const dimText = action === "request" || action === "requested" || action === "private-no-url";
		const isPrivate = doc.display === "private";

		const handleRowClick = () => {
			if (action === "view" && doc.storage_path) {
				window.open(doc.storage_path, "_blank", "noopener,noreferrer");
			} else if (action === "request") {
				if (isGuest) {
					setIsUpgradeModalOpen(true);
				} else {
					setRequestModalDocId(doc.id);
				}
			}
		};

		return (
			<div
				key={doc.id}
				className={`col-span-${colCount} grid grid-cols-subgrid items-center px-4 py-3 transition-colors hover:bg-blue-50 ${isClickable ? "cursor-pointer" : ""}`}
				onClick={isClickable ? handleRowClick : undefined}
			>
				<FiFile className="shrink-0 text-gray-400" size={18} />
				<div className="min-w-0">
					<p className={`text-sm font-medium ${dimText ? "text-gray-400" : "text-gray-900"}`}>
						{isPrivate ? doc.category.name : doc.name}
						{!isInsurance && isPrivate && (
							<span className="ml-1.5 text-xs text-gray-400">(Private)</span>
						)}
					</p>
					{doc.description && (
						<p className="text-xs text-gray-500">{doc.description}</p>
					)}
				</div>
				{isInsurance && (
					<>
						<span className={`pl-4 text-sm font-semibold tabular-nums ${dimText ? "text-gray-300" : "text-gray-500"}`}>
							{getInsuranceValue(isPrivate, doc.extra_info) ?? "—"}
						</span>
						<span className="text-xs text-gray-400">
							{isPrivate && "(Private)"}
						</span>
					</>
				)}
				<div className="flex items-center justify-end">{renderActionBadge(action)}</div>
			</div>
		);
	};

	return (
		<div>
			<h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>
			<div className={`grid ${gridCols} gap-x-3 divide-y divide-gray-100 rounded-lg border border-gray-200`}>
				{documents.map(renderRow)}
			</div>

			{requestModalDocId !== null && (
				<Modal
					isOpen
					onClose={() => setRequestModalDocId(null)}
					title="Request Document Access"
					iconType="info"
					size="lg"
				>
					<RequestAccessModal
						documentId={requestModalDocId}
						onSuccessfulSubmit={() => setRequestModalDocId(null)}
					/>
				</Modal>
			)}

			<RestrictedDocumentModal
				isOpen={isUpgradeModalOpen}
				setIsOpen={setIsUpgradeModalOpen}
			/>
		</div>
	);
};
